import * as React from 'react';
import {Core, Position} from 'cytoscape';
import {
    getDefaultEdgeStyles,
    getUnselectedNodeStyles,
    getHoveredNodeStyles,
    getSelectedNodeStyles,
    getUnhoveredNodeStyles,
    getSelectedEdgeStyles,
    getHiddenNodeStyles,
    getHiddenEdgeStyles,
    getUnhiddenNodeStyles,
    getUnhiddenEdgeStyles
} from './get-dynamic-styles';
import {useGraphStateDiff} from './use-graph-state-diff';
import {getEdgeCytoscapeId} from '../utils';
import {GraphEdge, GraphLayout, GraphNode} from '../interface';
import {createEdgeElement, createNodeElement} from './create-elements';
import {createPopoverForElement, destroyPopoverForElement} from './popover';
import {
    defaultPan,
    defaultZoom
} from '../../../application/query-workflow/query-layout/completed-query/results-graph/use-camera-props';

interface Props {
    nodes: GraphNode[];
    edges: GraphEdge[];
    nodePositions: GraphLayout;
    graphStateDiffProps: ReturnType<typeof useGraphStateDiff>;
    csRef: React.MutableRefObject<Core | null>;
    containerElementRef: React.MutableRefObject<HTMLDivElement | null>;
    zoom: number;
    pan: Position;
}

export function useDynamicStyles(props: Props) {
    const {graphStateDiffProps, nodes, edges, nodePositions, csRef, zoom, pan} = props;
    const {
        newSelectedNodes,
        newUnselectedNodes,
        newPartiallyHiddenNodes,
        newFullyHiddenNodes,
        newHoveredNodes,
        newUnhoveredNodes,
        newSelectedEdges,
        newPartiallyHiddenEdges,
        newFullyHiddenEdges,
        newUnselectedEdges,
        newPartiallyUnhiddenNodes,
        newFullyUnhiddenNodes,
        newPartiallyUnhiddenEdges,
        newFullyUnhiddenEdges,
        getIsNodeFullyHidden,
        getIsNodePartiallyHidden,
        getIsEdgePartiallyHidden,
        getIsEdgeFullyHidden,
        movedNodes
    } = graphStateDiffProps;
    const nodePositionsRef = React.useRef(nodePositions);
    nodePositionsRef.current = nodePositions;

    React.useEffect(() => {
        csRef.current?.batch(() => {
            newPartiallyHiddenNodes.forEach((nodeId) => {
                csRef.current?.getElementById(String(nodeId)).style(getHiddenNodeStyles());
            });

            newPartiallyUnhiddenNodes.forEach((nodeId) => {
                csRef.current?.getElementById(String(nodeId)).style(getUnhiddenNodeStyles());
            });

            newFullyHiddenNodes.forEach((nodeId) => {
                csRef.current?.getElementById(String(nodeId)).remove();
            });

            if (newFullyUnhiddenNodes) {
                const nodesToAdd = nodes.filter(
                    ({id}) =>
                        id === newFullyUnhiddenNodes.find((unhiddenNodeId) => unhiddenNodeId === id)
                )!;

                csRef.current?.add(
                    nodesToAdd.map((node) =>
                        createNodeElement(
                            node,
                            nodePositionsRef.current,
                            getIsNodePartiallyHidden,
                            false
                        )
                    )
                );

                const edgesToAdd = edges.filter(
                    ({startViewNodeId, endViewNodeId}) =>
                        (newFullyUnhiddenNodes.includes(startViewNodeId) &&
                            !getIsNodeFullyHidden(endViewNodeId)) ||
                        (newFullyUnhiddenNodes.includes(endViewNodeId) &&
                            !getIsNodeFullyHidden(startViewNodeId))
                );

                csRef.current?.add(
                    edgesToAdd.map((edge) =>
                        createEdgeElement(edge, getIsEdgePartiallyHidden, false)
                    )
                );
            }

            movedNodes.forEach((nodeId) => {
                csRef.current?.getElementById(String(nodeId)).position(nodePositions[nodeId]);
            });

            newSelectedNodes.forEach((nodeId) => {
                csRef.current
                    ?.getElementById(String(nodeId))
                    .style(getSelectedNodeStyles())
                    .select();
            });

            newUnselectedNodes.forEach((nodeId) => {
                csRef.current
                    ?.getElementById(String(nodeId))
                    .style(getUnselectedNodeStyles())
                    .unselect();
            });

            newHoveredNodes.forEach((nodeId) => {
                const el = csRef.current?.getElementById(String(nodeId));
                if (!el || !el.length) {
                    return;
                }

                el.style(getHoveredNodeStyles());

                createPopoverForElement(el, nodeId, props.containerElementRef.current);
            });

            newUnhoveredNodes.forEach((nodeId) => {
                const el = csRef.current?.getElementById(String(nodeId));

                if (!el || !el.length) {
                    return;
                }

                el.style(getUnhoveredNodeStyles());

                destroyPopoverForElement(nodeId);
            });

            newSelectedEdges.forEach((id) => {
                csRef.current
                    ?.getElementById(getEdgeCytoscapeId(id))
                    .style(getSelectedEdgeStyles());
            });

            newPartiallyHiddenEdges.forEach((id) => {
                csRef.current?.getElementById(getEdgeCytoscapeId(id)).style(getHiddenEdgeStyles());
            });

            newFullyHiddenEdges.forEach((edgeId) => {
                csRef.current?.getElementById(String(edgeId)).remove();
            });

            newPartiallyUnhiddenEdges.forEach((id) => {
                csRef.current
                    ?.getElementById(getEdgeCytoscapeId(id))
                    .style(getUnhiddenEdgeStyles());
            });

            newUnselectedEdges.forEach((id) => {
                csRef.current
                    ?.getElementById(getEdgeCytoscapeId(id))
                    .style(getDefaultEdgeStyles(getIsEdgePartiallyHidden(id)));
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        newSelectedNodes,
        newHoveredNodes,
        newUnhoveredNodes,
        newUnselectedNodes,
        newSelectedEdges,
        newUnselectedEdges,
        newPartiallyHiddenNodes,
        newFullyHiddenNodes,
        newPartiallyUnhiddenNodes,
        newFullyUnhiddenNodes,
        newPartiallyHiddenEdges,
        newFullyHiddenEdges,
        newPartiallyUnhiddenEdges,
        newFullyUnhiddenEdges,
        getIsEdgeFullyHidden,
        getIsEdgePartiallyHidden,
        nodes,
        edges,
        movedNodes
    ]);

    React.useEffect(() => {
        if (zoom !== Number(defaultZoom)) {
            csRef.current?.zoom(zoom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom]);

    React.useEffect(() => {
        if (JSON.stringify(pan) !== JSON.stringify(defaultPan)) {
            csRef.current?.pan(pan);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(pan)]);
}
